import { CheckCircle2, CircleDashed, FolderOpen, ListChecks, PlayCircle, XCircle } from "lucide-react";

import { Badge, Button } from "@workanywhere/ui";
import { cn } from "@workanywhere/ui";
import { formatTime, getTaskDisplayStatus, getTaskSourceLabel, type TaskDisplayStatus } from "../../agent-updates";
import type { AgentTaskRecord } from "../../../../../shared/agent";

export function TaskSidebar({
  errorMessage,
  loading,
  selectedTaskId,
  tasks,
  onOpenAnotherWorkspace,
  onSelectTask,
}: {
  errorMessage: string | null;
  loading: boolean;
  selectedTaskId: string | null;
  tasks: AgentTaskRecord[];
  onOpenAnotherWorkspace: () => void;
  onSelectTask: (taskId: string) => void;
}) {
  return (
    <aside className="flex min-h-0 flex-col overflow-hidden border-b bg-card/60 lg:h-full lg:border-b-0 lg:border-r">
      <div className="shrink-0 border-b p-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold">Tasks</h2>
          <Badge variant="secondary">{tasks.length}</Badge>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{loading ? "Loading tasks" : "Newest first"}</p>
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-2">
        {errorMessage ? <p className="p-2 text-sm text-destructive">{errorMessage}</p> : null}
        {!errorMessage && tasks.length === 0 ? (
          <div className="flex h-full min-h-48 items-center justify-center p-4 text-center text-sm text-muted-foreground">
            No tasks yet.
          </div>
        ) : null}
        <div className="flex flex-col gap-1">
          {tasks.map((task) => (
            <TaskRow
              key={task.id}
              selected={task.id === selectedTaskId}
              task={task}
              onSelect={() => onSelectTask(task.id)}
            />
          ))}
        </div>
      </div>

      <div className="shrink-0 border-t p-2">
        <Button type="button" variant="outline" className="w-full justify-start" onClick={onOpenAnotherWorkspace}>
          <FolderOpen className="size-4" />
          Open another workspace
        </Button>
      </div>
    </aside>
  );
}

function TaskRow({ selected, task, onSelect }: { selected: boolean; task: AgentTaskRecord; onSelect: () => void }) {
  const status = getTaskDisplayStatus(task);
  const sourceLabel = getTaskSourceLabel(task);
  const StatusIcon = getStatusIcon(status);

  return (
    <button
      type="button"
      aria-current={selected ? "true" : undefined}
      onClick={onSelect}
      className={cn(
        "w-full rounded-md border border-transparent px-3 py-2 text-left transition-colors hover:bg-accent/70",
        selected && "border-border bg-background shadow-xs",
      )}
    >
      <div className="flex min-w-0 items-start justify-between gap-2">
        <span className="truncate text-sm font-medium">{task.title}</span>
        <span className="flex shrink-0 items-center gap-1">
          {sourceLabel ? <Badge variant="secondary">{sourceLabel}</Badge> : null}
          <Badge
            variant="outline"
            className={cn(
              "gap-1 capitalize",
              status === "running" && "border-emerald-200 bg-emerald-50 text-emerald-700",
            )}
          >
            <StatusIcon className="size-3" />
            {statusLabel(status)}
          </Badge>
        </span>
      </div>
      <div className="mt-2 flex items-center justify-between gap-3 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <ListChecks className="size-3.5" />
          {task.runs.length} {task.runs.length === 1 ? "run" : "runs"}
        </span>
        <span>{formatTime(task.updatedAt)}</span>
      </div>
    </button>
  );
}

function statusLabel(status: TaskDisplayStatus): string {
  return status === "no-runs" ? "No runs" : status;
}

function getStatusIcon(status: TaskDisplayStatus) {
  if (status === "running") return PlayCircle;
  if (status === "completed") return CheckCircle2;
  if (status === "failed") return XCircle;
  return CircleDashed;
}
